import { Component, input, signal, effect, ChangeDetectorRef, output, ChangeDetectionStrategy } from "@angular/core";

import { FormsModule } from "@angular/forms";
import { CacheRepository } from "../../decorators/cache.decorator";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25RuleTreeModule } from "../s25-rule-tree/s25.rule.tree.module";
import { DataIngestionService } from "../data-ingestion/data.ingestion.service";
import { INGESTION_POINT_TYPE_ID } from "../data-ingestion/data.ingestion.const";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { RuleTreeService } from "../../services/rule.tree.service";

import { Rules } from "../s25-rule-tree/s25.rule.const";
import { ModalService } from "../modal/modal.service";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../pojo/DropDownItem";

interface FormWithRules {
    formId: number;
    formName: string;
}

@TypeManagerDecorator("workflow-stage-rules")
@Component({
    selector: "workflow-stage-rules",
    template: `
        @if (selectedStage()) {
            <div class="rules-header">
                <h3>Rules for {{ selectedStage()?.stageName }}</h3>
            </div>

            @for (form of rulesByForm(); track form.formId) {
                <div class="form-rules-section">
                    <div class="form-header">
                        <label>Form: {{ form.formName }}</label>
                    </div>
                    <div class="rules-editor">
                        <s25-ng-rule-tree
                            [category]="'workflowStage'"
                            [subCategory]="getSubCategory(form.formId)"
                            [subCategoryPrefix]="getStagePrefix()"
                            [conditionFilterMap]="getConditionFilterMap(form.formId)"
                            class="rule-tree-editor"
                        ></s25-ng-rule-tree>
                    </div>
                </div>
            }

            <div class="add-rule-section">
                <label>Add Rule for Form:</label>
                <div class="form-selector-row">
                    <s25-ingestion-point-dropdown
                        [value]="selectedFormDropdownItem()"
                        [enabledOnly]="true"
                        (valueChange)="onFormSelected($event)"
                        class="form-dropdown"
                    />
                    <button
                        class="aw-button aw-button--primary"
                        (click)="onAddRuleForForm()"
                        [disabled]="!selectedFormDropdownItem()"
                    >
                        Add Rule
                    </button>
                </div>
            </div>
        } @else {
            <div class="no-stage-selected">
                <p>No stage selected.</p>
            </div>
        }
    `,
    styles: `
        .rules-header h3 {
            margin: 0 0 16px 0;
            color: var(--color--text-strong);
        }

        .form-rules-section {
            margin-bottom: 32px;
        }

        .form-header {
            padding: 16px;
            background: var(--color--surface-2-bg);
            border-radius: 6px 6px 0 0;
            border: 1px solid var(--color--border);
            border-bottom: none;
        }

        .form-header label {
            display: block;
            font-weight: 500;
            margin: 0;
            color: var(--color--text);
        }

        .rules-editor {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 0 0 6px 6px;
            padding: 20px;
        }

        .add-rule-section {
            margin-top: 32px;
            margin-bottom: 200px;
            padding: 20px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 6px;
            overflow: visible;
        }

        .add-rule-section label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .form-selector-row {
            display: flex;
            gap: 12px;
            align-items: center;
        }

        .form-dropdown {
            flex: 1;
            min-width: 200px;
        }

        @media (max-width: 768px) {
            .form-selector-row {
                flex-direction: column;
                align-items: stretch;
            }

            .form-dropdown {
                min-width: 100%;
            }

            .form-selector-row button {
                width: 100%;
            }
        }

        .no-stage-selected {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
            background: var(--color--surface-2-bg);
            border-radius: 6px;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, S25RuleTreeModule, S25DropdownPaginatedModule],
})
export class WorkflowStageRulesComponent {
    selectedStage = input<WorkflowTypes.WorkflowStage | null>(null);
    workflowProcessId = input<number | null>(null);
    /** Emitted when rules are created or updated (e.g. modal save) so parents can refresh stage lists. */
    readonly rulesChanged = output<void>();

    rulesByForm = signal<FormWithRules[]>([]);
    selectedFormDropdownItem = signal<DropDownItem | null>(null);
    private formNamesCache = new Map<number, string>();

    constructor(private cd: ChangeDetectorRef) {
        effect(() => {
            this.selectedStage();
            this.workflowProcessId();
            this.loadAllRules();
        });
    }

    async loadAllRules() {
        if (!this.selectedStage()?.workflowStageId) return;

        CacheRepository.invalidateByStartsWith("RuleTreeService.getRulesByPrefix");
        const prefix = `${this.selectedStage()?.workflowStageId}&`;
        const rawRules = await RuleTreeService.getRulesByPrefix("workflowStage", prefix, true);

        const formIds = new Set<number>();
        for (const rule of rawRules) {
            const formId = rule.sub_cat ? this.extractFormId(rule.sub_cat) : null;
            if (formId) formIds.add(formId);
        }

        const missing = [...formIds].filter((id) => !this.formNamesCache.has(id));
        if (missing.length > 0) {
            const fetched = await DataIngestionService.getIngestionPointNames(missing);
            for (const [id, name] of fetched) {
                this.formNamesCache.set(id, name);
            }
        }

        this.rulesByForm.set(
            [...formIds].map((id) => ({
                formId: id,
                formName: this.formNamesCache.get(id) || `Form ${id}`,
            })),
        );
        this.cd.detectChanges();
    }

    extractFormId(subCategory: string): number | null {
        const parts = subCategory?.split("&");
        const id = parseInt(parts?.[2]);
        return Number.isFinite(id) ? id : null;
    }

    onFormSelected(item: DropDownItem | undefined) {
        if (!item) return;
        this.selectedFormDropdownItem.set(item);
    }

    onAddRuleForForm() {
        const item = this.selectedFormDropdownItem();
        if (!item?.itemId) return;
        const formId = typeof item.itemId === "number" ? item.itemId : parseInt(item.itemId);

        const newRule: Rules.Rule = {
            id: -1 * Math.ceil((Math.random() + 1) * 100000000),
            name: "New Rule",
            description: "",
            active: true,
            category: "workflowStage",
            subCategory: this.getSubCategory(formId),
            targets: {
                enable: [],
                disable: [],
            },
            conditions: {
                operator: "and",
                children: [],
            },
            conditionFilterMap: this.getConditionFilterMap(formId),
        };

        ModalService.modal("edit-rule", {
            rule: newRule,
            title: "Create New Rule",
            isNew: true,
            refresh: async () => {
                CacheRepository.invalidateByStartsWith("RuleTreeService.getRulesByPrefix");
                await this.loadAllRules();
                this.rulesChanged.emit();
            },
        });
    }

    getStagePrefix(): string {
        if (!this.selectedStage()?.workflowStageId) return "";
        return `${this.selectedStage()?.workflowStageId}&`;
    }

    getSubCategory(formId: number): string {
        if (!this.selectedStage()?.workflowStageId) return "";
        return `${this.selectedStage()?.workflowStageId}&${INGESTION_POINT_TYPE_ID}&${formId}`;
    }

    getConditionFilterMap(formId: number): Record<string, string> {
        return {
            ingestionFieldsAll: "" + formId,
        };
    }
}
